import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Palette, Users, Award, Calendar, Clock, AlertCircle } from 'lucide-react';
import SEOHead, { CourseSchema, FAQSchema } from '../components/SEOHead';
import { sanitizeHtml, stripHtmlToText } from '../utils/sanitize';
import { isProgramPublic } from '../utils/programVisibility';
import { isPrivateLesson, getLowestSessionPrice } from '../utils/privateLesson';
import { PrivateLessonBanner } from '../components/PrivateLessonBanner';
import { mediaUrl, resolveImage } from '../utils/mediaUrl';

const API_URL = "";

const KIDS_FAQS = [
  { q: "What ages are the kids art classes for?", a: "Classes are available for children ages 5–18, grouped by grade level: Kindergarten–2nd grade, 3rd–5th grade, 6th–8th grade, and 9th–12th grade." },
  { q: "What days are kids classes held?", a: "Classes meet Wednesdays, Fridays, and Saturdays — a mix of after-school and weekend options. Specific times vary by age group; see the class cards above for the schedule of each session." },
  { q: "How much do kids art classes cost?", a: "Tuition is priced per 12-week semester. View our current schedule and pricing above, or call (210) 530-8445 for details." },
  { q: "Do kids need to bring their own supplies?", a: "We provide a supply list before the semester begins so you know exactly what to purchase. Most supplies are inexpensive and reusable." },
  { q: "Where are classes located?", a: "All classes are held at The Coppini Academy of Fine Art, 115 Melrose Place, San Antonio, TX 78212 in the Monte Vista historic neighborhood." }
];

export const KidsClasses = () => {
  const [programs, setPrograms] = useState([]);
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [programsRes, instructorsRes] = await Promise.all([
          fetch(`${API_URL}/api/content/programs`),
          fetch(`${API_URL}/api/content/instructors`)
        ]);
        if (programsRes.ok) setPrograms(await programsRes.json());
        if (instructorsRes.ok) setInstructors(await instructorsRes.json());
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const kidsClasses = programs.filter(program => program.category === 'Youth' && isProgramPublic(program));

  // Private lessons have no term or dates — they sit in their own group at the end.
  const privateLessons = kidsClasses.filter(isPrivateLesson);
  const termClasses = kidsClasses.filter(p => !isPrivateLesson(p));

  // Upcoming-term classes (start date still in the future) are listed first in
  // calendar order so families see what they can enrol in next. Everything else
  // (currently running / undated rolling classes) follows, newest added first.
  const todayIso = new Date().toISOString().slice(0, 10);
  const isUpcomingTerm = (p) => typeof p.startDate === 'string' && p.startDate > todayIso;
  const upcomingClasses = termClasses
    .filter(isUpcomingTerm)
    .sort((a, b) => a.startDate.localeCompare(b.startDate));
  const currentClasses = termClasses
    .filter(p => !isUpcomingTerm(p))
    .sort((a, b) => b.id - a.id);

  const upcomingTerms = [...new Set(upcomingClasses.map(p => p.term_of).filter(Boolean))];
  const upcomingHeading = upcomingTerms.length === 1
    ? `Enrolling Now for ${upcomingTerms[0]}`
    : 'Enrolling Now — Upcoming Term';

  const groupCount = [upcomingClasses, currentClasses, privateLessons].filter(g => g.length > 0).length;

  const classGroups = [
    upcomingClasses.length > 0 && {
      key: 'upcoming',
      heading: upcomingHeading,
      subtext: 'Registration is open — secure your child\u2019s spot for the upcoming term.',
      items: upcomingClasses,
    },
    currentClasses.length > 0 && {
      key: 'current',
      // Only labelled when there is another group to contrast with
      heading: groupCount > 1 ? 'Currently Running' : null,
      subtext: 'These sessions are already underway \u2014 ask us about joining late or reserving a spot next term.',
      items: currentClasses,
    },
    privateLessons.length > 0 && {
      key: 'private',
      heading: groupCount > 1 ? 'Private Lessons — Available Year-Round' : null,
      subtext: 'One-on-one sessions booked whenever suits you \u2014 no term, no fixed day or time.',
      items: privateLessons,
    },
  ].filter(Boolean);

  // Youth instructors – render in the order defined here
  const youthInstructorOrder = ['Andrea Perantoni'];
  const youthInstructors = youthInstructorOrder
    .map(name => instructors.find(i => i.name === name))
    .filter(Boolean);

  const youthInstructorMeta = {
    'Andrea Perantoni': {
      label: 'Youth Program Instructor',
      tags: ['BFA in Painting', 'UTSA', 'Oil Painter'],
    },
  };

  const slugify = (name) => (name || '').replace(/\s+/g, '-').toLowerCase();
  const youthInstructorNames = new Set(youthInstructors.map(i => i.name));

  const scrollToInstructor = (name) => {
    const el = document.getElementById(`instructor-${slugify(name)}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Format YYYY-MM-DD safely as "Mon D" (avoids timezone-shift bugs from new Date('YYYY-MM-DD'))
  const formatShortDate = (iso) => {
    if (!iso || typeof iso !== 'string') return '';
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return '';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const monthIdx = parseInt(m[2], 10) - 1;
    const day = parseInt(m[3], 10);
    if (monthIdx < 0 || monthIdx > 11) return '';
    return `${months[monthIdx]} ${day}`;
  };

  // Build the days-and-dates line for the schedule banner
  const buildDaysAndDates = (program) => {
    const days = program.daysOfWeek || '';
    const start = formatShortDate(program.startDate);
    const end = formatShortDate(program.endDate);
    if (start && end) return `${days ? days + ' · ' : ''}${start} – ${end}`;
    if (start) return `${days ? days + ' · starting ' : 'Starting '}${start}`;
    return program.schedule || days || '';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-stone-600">Loading classes...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="kids-classes-page">
      <SEOHead
        title="Kids Art Classes San Antonio | Ages 5–18 | Coppini Academy"
        description="After-school and weekend kids art classes in San Antonio for ages 5–18. Classical drawing, painting & sculpture in small groups of 8–10 students. Wed/Fri/Sat. Call (210) 530-8445."
        keywords="kids art classes San Antonio, kids art classes, after school art classes San Antonio, art classes for kids near me, children art lessons, youth art San Antonio"
        canonicalPath="/classes/kids"
      />
      {kidsClasses.map((program) => (
        <CourseSchema
          key={`course-schema-${program.id}`}
          name={program.title}
          description={stripHtmlToText(program.description).slice(0, 500) || program.title}
          price={isPrivateLesson(program) ? undefined : program.price}
          startDate={program.startDate}
          endDate={program.endDate}
          instructor={program.instructor}
        />
      ))}
      <FAQSchema faqs={KIDS_FAQS.map(f => ({ question: f.q, answer: f.a }))} />
      {/* Hero */}
      <section className="bg-gradient-to-b from-stone-50 to-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-stone-900 mb-4">
            Kids Art Classes in San Antonio
          </h1>
          <img 
            src="https://customer-assets.emergentagent.com/job_fine-art-academy-2/artifacts/bva2iwcs_CAFA_KIDS_.png"
            alt="Coppini Kids — Art Classes for Kids in San Antonio"
            className="h-24 sm:h-32 mx-auto mb-4"
          />
          <p className="text-xl text-stone-600">
            Classical drawing & painting for young artists ages 5–18 in San Antonio
          </p>
          <p className="text-stone-500 mt-2 text-sm">
            Small groups of 8–10 students &nbsp;·&nbsp; After School and Weekends, Wed/Fri/Sat &nbsp;·&nbsp; Nonprofit since 1945
          </p>
          <p className="text-stone-500 mt-4">
            <Link to="/classes/adult" className="text-cafa-red hover:text-cafa-red-900 underline">
              Looking for Adult Classes? →
            </Link>
          </p>
        </div>
      </section>

      {/* Program Highlights */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center p-6 bg-cafa-red/90 rounded-2xl">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Palette className="text-white" size={24} />
              </div>
              <h3 className="font-semibold text-white mb-2">Multiple Mediums</h3>
              <p className="text-sm text-white/80">Oil, acrylic, watercolor, pastel & drawing</p>
            </div>
            <div className="text-center p-6 bg-cafa-red/90 rounded-2xl">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="text-white" size={24} />
              </div>
              <h3 className="font-semibold text-white mb-2">Age-Appropriate Groups</h3>
              <p className="text-sm text-white/80">Classes grouped by grade level</p>
            </div>
            <div className="text-center p-6 bg-cafa-red/90 rounded-2xl">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="text-white" size={24} />
              </div>
              <h3 className="font-semibold text-white mb-2">Exhibition & Reception</h3>
              <p className="text-sm text-white/80">End-of-semester showcase for families</p>
            </div>
            <div className="text-center p-6 bg-cafa-red/90 rounded-2xl">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="text-white" size={24} />
              </div>
              <h3 className="font-semibold text-white mb-2">12-Week Sessions</h3>
              <p className="text-sm text-white/80">Wed / Fri / Sat — after school & weekends</p>
            </div>
          </div>
        </div>
      </section>

      {/* Classes by Age Group */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-4 text-center">Classes by Age Group</h2>
          <p className="text-stone-600 text-center mb-12 max-w-2xl mx-auto">
            Each class is tailored to the developmental level and artistic abilities of the age group, 
            ensuring every young artist gets the most out of their experience.
          </p>
          
          {kidsClasses.length === 0 ? (
            <div className="text-center text-stone-600 py-12">
              <p>No kids classes are currently scheduled. Please check back later!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {classGroups.flatMap((group) => [
                group.heading ? (
                  <div
                    key={`heading-${group.key}`}
                    className="md:col-span-2 border-l-4 border-cafa-red pl-4 py-1 mt-2 first:mt-0"
                    data-testid={`kids-group-heading-${group.key}`}
                  >
                    <h3 className="text-2xl font-serif text-stone-900">{group.heading}</h3>
                    <p className="text-sm text-stone-600 mt-1">{group.subtext}</p>
                  </div>
                ) : null,
                ...group.items.map((program) => {
                const cardImage = resolveImage(program, 'image_ref', 'images', 'classImage', 'instructorImage');
                return (
                <Card key={program.id} className="border-stone-200 hover:shadow-lg transition-shadow overflow-hidden" data-testid={`kids-class-card-${program.id}`}>
                  {/* Class image (preferred) or fallback to instructor image */}
                  <div className="h-48 overflow-hidden bg-stone-100">
                    {cardImage ? (
                      <img 
                        src={mediaUrl(cardImage, 'card')} 
                        alt={program.title}
                        className="w-full h-full object-cover"
                        style={{ 
                          objectPosition: program.title.includes('3rd to 5th') ? 'center 30%' : 'center center' 
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.innerHTML = '<div class="w-full h-full flex items-center justify-center text-stone-400"><svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg></div>';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-stone-400">
                        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                          <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
                          <circle cx="9" cy="9" r="2"/>
                          <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
                        </svg>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="px-3 py-1 bg-cafa-red-100 text-cafa-red-900 text-xs font-medium rounded-full">
                        {program.level}
                      </span>
                    </div>
                    <h3 className="text-xl font-semibold text-stone-900 mb-3">{program.title}</h3>

                    {/* Private lesson banner, or the prominent schedule banner (dates + time) */}
                    {isPrivateLesson(program) ? (
                      <PrivateLessonBanner program={program} />
                    ) : (buildDaysAndDates(program) || program.time) ? (
                      <div
                        className="mb-4 rounded-md border border-cafa-red/20 bg-cafa-red/5 px-3 py-2.5 flex flex-col sm:flex-row sm:items-center sm:gap-4 gap-1.5"
                        data-testid={`kids-class-schedule-${program.id}`}
                      >
                        {buildDaysAndDates(program) && (
                          <div className="flex items-center gap-2 text-sm font-medium text-stone-800">
                            <Calendar size={16} className="text-cafa-red flex-shrink-0" />
                            <span>{buildDaysAndDates(program)}</span>
                          </div>
                        )}
                        {program.time && (
                          <div className="flex items-center gap-2 text-sm font-medium text-stone-800">
                            <Clock size={16} className="text-cafa-red flex-shrink-0" />
                            <span>{program.time}</span>
                          </div>
                        )}
                      </div>
                    ) : null}

                    <div
                      className="text-sm text-stone-600 mb-4 prose prose-sm prose-stone max-w-none [&_p]:mb-2 [&_p:last-child]:mb-0"
                      data-testid={`kids-class-description-${program.id}`}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(program.description) }}
                    />
                    <div className="space-y-2 text-sm text-stone-500 mb-4">
                      {!isPrivateLesson(program) && (
                        <p><strong className="text-stone-700">Duration:</strong> {program.duration}</p>
                      )}
                      <p>
                        <strong className="text-stone-700">Instructor:</strong>{' '}
                        {youthInstructorNames.has(program.instructor) ? (
                          <button
                            type="button"
                            onClick={() => scrollToInstructor(program.instructor)}
                            className="text-cafa-red hover:text-cafa-red-900 underline underline-offset-2"
                            data-testid={`meet-instructor-link-${program.id}`}
                          >
                            {program.instructor}
                          </button>
                        ) : (
                          program.instructor
                        )}
                      </p>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-stone-200">
                      {isPrivateLesson(program) ? (
                        <>
                          <span className="text-2xl font-bold text-cafa-red">
                            From ${getLowestSessionPrice(program)}
                            <span className="block text-xs font-normal text-stone-500">per session</span>
                          </span>
                          <Button
                            onClick={() => navigate(`/register-class?class=${program.id}`)}
                            className="bg-cafa-red hover:bg-cafa-red-900 text-white"
                            data-testid={`kids-book-private-btn-${program.id}`}
                          >
                            Book a Private Lesson
                          </Button>
                        </>
                      ) : (
                        <>
                      <span className="text-2xl font-bold text-cafa-red">${program.price}</span>
                      {/* Enrollment Managed Classes - Live Registration */}
                      {program.class_type === 'enrollment_managed' ? (
                        <Button 
                          onClick={() => navigate(`/register-class?class=${program.id}`)}
                          className="bg-cafa-red hover:bg-cafa-red-900 text-white"
                          data-testid={`kids-enroll-btn-${program.id}`}
                        >
                          Enroll Now
                        </Button>
                      ) : program.payment_method === 'paypal' && program.price > 0 ? (
                        <Button
                          onClick={() => navigate(`/register-class?class=${program.id}`)}
                          className="bg-cafa-red hover:bg-cafa-red-900 text-white"
                          data-testid={`kids-paypal-btn-${program.id}`}
                        >
                          Register &amp; Pay
                        </Button>
                      ) : program.class_type === 'space_rental' ? (
                        <Button 
                          asChild
                          variant="outline"
                          className="border-stone-300 text-stone-700"
                          data-testid={`kids-inquire-btn-${program.id}`}
                        >
                          <a href={`mailto:${program.instructorEmail}?subject=Space Rental Inquiry: ${program.title}`}>
                            Inquire
                          </a>
                        </Button>
                      ) : (
                        /* Informational Classes - Contact for more info */
                        <Button 
                          asChild
                          variant="outline"
                          className="border-stone-300 text-stone-700"
                          data-testid={`kids-learn-btn-${program.id}`}
                        >
                          <a href={`mailto:${program.instructorEmail}?subject=Class Inquiry: ${program.title}`}>
                            Learn More
                          </a>
                        </Button>
                      )}
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
                );
              }),
              ].filter(Boolean))}
            </div>
          )}
        </div>
      </section>

      {/* Instructor Section */}
      {youthInstructors.length > 0 && (
        <section className="py-16 bg-stone-50">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
            {youthInstructors.map((inst) => {
              const meta = youthInstructorMeta[inst.name] || { label: 'Youth Program Instructor', tags: [] };
              return (
                <div
                  key={inst.id || inst.name}
                  id={`instructor-${slugify(inst.name)}`}
                  className="bg-white rounded-2xl shadow-lg overflow-hidden scroll-mt-24"
                  data-testid={`youth-instructor-card-${slugify(inst.name)}`}
                >
                  <div className="md:flex">
                    <div className="md:w-1/3 bg-cafa-red-100 flex items-center justify-center p-8">
                      <img
                        src={mediaUrl(inst.imageUrl, 'avatar')}
                        alt={inst.name}
                        className="w-48 h-48 rounded-full object-cover shadow-lg"
                      />
                    </div>
                    <div className="md:w-2/3 p-8">
                      <span className="text-cafa-red font-medium text-sm uppercase tracking-wide">{meta.label}</span>
                      <h2 className="text-3xl font-serif text-stone-900 mt-2 mb-4">{inst.name}</h2>
                      <p className="text-stone-600 mb-6">{inst.bio}</p>
                      <div className="flex flex-wrap gap-2">
                        {meta.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-3 py-1 bg-cafa-red-100 text-cafa-red-900 text-sm rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* What Kids Learn */}
      <section className="py-16 bg-cafa-red-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-12 text-center">What Your Child Will Learn</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {['Oil Painting', 'Acrylic Painting', 'Life Drawing', 'Watercolor', 'Pastel', 'Fundamentals'].map((skill) => (
              <div key={skill} className="text-center">
                <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm">
                  <Palette className="text-cafa-red" size={32} />
                </div>
                <p className="text-sm font-medium text-stone-700">{skill}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-stone-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-serif mb-4">Ready to Enroll Your Child?</h2>
          <p className="text-stone-300 mb-2">
            Contact us to learn more about our kids art classes or to register for the next semester.
          </p>
          <p className="text-stone-400 text-sm mb-8">
            115 Melrose Place, San Antonio, TX 78212 &nbsp;·&nbsp; <a href="tel:+12105308445" className="underline hover:text-white">(210) 530-8445</a>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild className="bg-cafa-red hover:bg-cafa-red-700 text-white">
              <a href="mailto:info@thecoppini.org?subject=Kids Class Inquiry">
                Email Us
              </a>
            </Button>
            <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-stone-900">
              <a href="tel:+12105308445">Call (210) 530-8445</a>
            </Button>
            <Button asChild variant="outline" className="border-white text-white hover:bg-white hover:text-stone-900">
              <Link to="/contact">Contact Page</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ for SEO */}
      <section className="py-16 bg-stone-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {KIDS_FAQS.map((faq, i) => (
              <div key={i} className="border-b border-stone-200 pb-5">
                <h3 className="font-semibold text-stone-900 mb-2">{faq.q}</h3>
                <p className="text-stone-600 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
          <p className="text-center mt-8">
            <Link to="/kids-art-classes-san-antonio" className="text-cafa-red underline hover:text-cafa-red-900 text-sm">
              Learn more about the Coppini Kids program →
            </Link>
          </p>
        </div>
      </section>
    </div>
  );
};
